import { format } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";
import parseDateYmd from "lib/utils/parseDateYmd";
import capitalize from "lodash/fp/capitalize";
import type { ReactElement } from "react";
import * as React from "react";
import type { IBrazilianRealEstateFundTopLevelFragment } from "../../lib/fragments/__generated__/BrazilianRealEstateFundTopLevel.generated";
import * as palette from "../palette";

interface IDistributionOfAssetsProps {
  realEstateFund: IBrazilianRealEstateFundTopLevelFragment;
}

export default function DistributionOfAssets({ realEstateFund }: IDistributionOfAssetsProps): ReactElement | null {
  if (!realEstateFund.distributionOfAssets) {
    return null;
  }

  const formattedDateOfReference = capitalize(
    format(parseDateYmd(realEstateFund.distributionOfAssets.dateOfReference), "MMMM/yyyy", {
      locale: ptBR,
    }),
  );

  return (
    <div>
      <div
        css={{
          display: "inline-grid",
          gridRowGap: 12,
        }}
      >
        <div>
          <div
            css={{
              display: "inline-grid",
              alignItems: "center",
              backgroundColor: palette.catskillWhite,
              padding: "8px 24px",
              borderTopRightRadius: 18,
              borderBottomRightRadius: 18,
              gridAutoColumns: "max-content",
              gridAutoFlow: "column",
              gridColumnGap: 8,
            }}
          >
            <PieChartIcon />

            <h4
              css={{
                margin: 0,
                lineHeight: 1.33,
                fontWeight: "bold",
                fontSize: 14,
                letterSpacing: 0.6,
                color: palette.fiord,
              }}
            >
              Distribuição dos Ativos
            </h4>
          </div>
        </div>

        <div
          css={{
            display: "grid",
            padding: "0px 24px",
            gridAutoColumns: "max-content",
            gridAutoFlow: "column",
            gridColumnGap: 28,
          }}
        >
          {Math.round(realEstateFund.distributionOfAssets.cash * 100) > 0 && (
            <div
              css={{
                display: "inline-grid",
                gridRowGap: 8,
                textAlign: "center",
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "100%",
                  border: `solid 1px ${palette.fiord}`,
                  fontSize: 14,
                  fontWeight: "bold",
                  lineHeight: 1.333,
                  letterSpacing: 0.6,
                  color: palette.fiord,
                }}
              >
                <span>{Math.round(realEstateFund.distributionOfAssets.cash * 100)}%</span>
              </div>

              <strong
                css={{
                  fontSize: 14,
                  fontWeight: "bold",
                  lineHeight: 1.333,
                  letterSpacing: 0.6,
                  color: palette.fiord,
                }}
              >
                Caixa
              </strong>
            </div>
          )}

          {Math.round(realEstateFund.distributionOfAssets.realEstate * 100) > 0 && (
            <div
              css={{
                display: "inline-grid",
                gridRowGap: 8,
                textAlign: "center",
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "100%",
                  border: `solid 1px ${palette.fiord}`,
                  fontSize: 14,
                  fontWeight: "bold",
                  lineHeight: 1.333,
                  letterSpacing: 0.6,
                  color: palette.fiord,
                }}
              >
                <span>{Math.round(realEstateFund.distributionOfAssets.realEstate * 100)}%</span>
              </div>

              <strong
                css={{
                  fontSize: 14,
                  fontWeight: "bold",
                  lineHeight: 1.333,
                  letterSpacing: 0.6,
                  color: palette.fiord,
                }}
              >
                Imóveis
              </strong>
            </div>
          )}

          {Math.round(realEstateFund.distributionOfAssets.fundsOfFunds * 100) > 0 && (
            <div
              css={{
                display: "inline-grid",
                gridRowGap: 8,
                textAlign: "center",
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "100%",
                  border: `solid 1px ${palette.fiord}`,
                  fontSize: 14,
                  fontWeight: "bold",
                  lineHeight: 1.333,
                  letterSpacing: 0.6,
                  color: palette.fiord,
                }}
              >
                <span>{Math.round(realEstateFund.distributionOfAssets.fundsOfFunds * 100)}%</span>
              </div>

              <strong
                css={{
                  fontSize: 14,
                  fontWeight: "bold",
                  lineHeight: 1.333,
                  letterSpacing: 0.6,
                  color: palette.fiord,
                }}
              >
                Fundos
              </strong>
            </div>
          )}

          {Math.round(realEstateFund.distributionOfAssets.otherInvestments * 100) > 0 && (
            <div
              css={{
                display: "inline-grid",
                gridRowGap: 8,
                textAlign: "center",
              }}
            >
              <div
                css={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 48,
                  height: 48,
                  borderRadius: "100%",
                  border: `solid 1px ${palette.fiord}`,
                  fontSize: 14,
                  fontWeight: "bold",
                  lineHeight: 1.333,
                  letterSpacing: 0.6,
                  color: palette.fiord,
                }}
              >
                <span>{Math.round(realEstateFund.distributionOfAssets.otherInvestments * 100)}%</span>
              </div>

              <strong
                css={{
                  fontSize: 14,
                  fontWeight: "bold",
                  lineHeight: 1.333,
                  letterSpacing: 0.6,
                  color: palette.fiord,
                }}
              >
                Papéis
              </strong>
            </div>
          )}
        </div>

        <div
          css={{
            padding: "0px 24px",
          }}
        >
          <p
            css={{
              margin: 0,
              fontSize: 12,
              fontWeight: 500,
              letterSpacing: 0.6,
              color: palette.baliHai,
              lineHeight: 1.33,
            }}
          >
            Referência: {formattedDateOfReference}
          </p>
        </div>
      </div>
    </div>
  );
}

function PieChartIcon(props: React.SVGProps<any>) {
  return (
    <svg width="1em" height="1em" viewBox="0 0 19 19" {...props}>
      <g fill="#4E657F" fillRule="nonzero">
        <path d="M16.4 9.966H9.009V2.573a.535.535 0 0 0-.538-.538c-4.668 0-8.468 3.8-8.468 8.469 0 4.668 3.8 8.468 8.468 8.468 4.669 0 8.469-3.8 8.469-8.468a.54.54 0 0 0-.538-.538zm-7.93 7.93c-4.075 0-7.393-3.317-7.393-7.392A7.4 7.4 0 0 1 7.933 3.13v7.373c0 .299.239.538.537.538h7.373a7.4 7.4 0 0 1-7.373 6.855z" />
        <path d="M18.97 8.44a8.482 8.482 0 0 0-2.119-5.576A8.474 8.474 0 0 0 10.501 0a.535.535 0 0 0-.537.538v7.93c0 .3.239.538.538.538h7.93a.535.535 0 0 0 .538-.538V8.44zm-7.93-.505V1.099a7.39 7.39 0 0 1 5.002 2.482 7.386 7.386 0 0 1 1.833 4.354H11.04z" />
      </g>
    </svg>
  );
}
